import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';

import { BaseResponseDto } from '@/dtos/responses/base.response.dto';

import api from '.';
import { CheckVerifyEmailRequestDto } from '../dtos/requests/check-verify-email.request.dto';
import { ForgotPasswordRequestDto } from '../dtos/requests/forgot-password.request.dto';
import { ResetForgotPasswordRequestDto } from '../dtos/requests/reset-forgot-password.request.dto';
import { SendVerifyEmailRequestDto } from '../dtos/requests/send-verify-email.request.dto';
import { SignInRequestDto } from '../dtos/requests/sign-in.request.dto';
import { SignUpRequestDto } from '../dtos/requests/sign-up.request.dto';
import { UpdateMyRequestDto } from '../dtos/requests/update-my.request.dto';
import { EmptyResponseDto } from '../dtos/responses/empty.response.dto';
import { GetMyResponseDto } from '../dtos/responses/get-my.response.dto';
import { SignInResponseDto } from '../dtos/responses/sign-in.response.dto';

/**
 *
 * @param data
 * @returns
 */
const signUp = (data: SignUpRequestDto): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, SignUpRequestDto>('/student/auth/sign-up', {
    method: 'POST',
    data,
  });
};

/**
 *
 * @param options
 * @returns
 */
export const useSignUp = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, SignUpRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, SignUpRequestDto>({
    mutationFn: signUp,
    ...options, // 전달받은 옵션을 병합
  });
};

const signIn = (data: SignInRequestDto): Promise<BaseResponseDto<SignInResponseDto>> => {
  return api<BaseResponseDto<SignInResponseDto>, SignInRequestDto>('/student/auth/sign-in', {
    method: 'POST',
    data,
  });
};

export const useSignIn = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<SignInResponseDto>, unknown, SignInRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<SignInResponseDto>, unknown, SignInRequestDto>({
    mutationFn: signIn,
    ...options, // 전달받은 옵션을 병합
  });
};

export const getMy = (): Promise<BaseResponseDto<GetMyResponseDto>> => {
  return api<BaseResponseDto<GetMyResponseDto>, Error>('/student/auth/my', {
    method: 'GET',
  });
};

export const useGetMy = (
  options?: Omit<UseQueryOptions<BaseResponseDto<GetMyResponseDto>, Error>, 'queryFn'>,
) => {
  return useQuery({
    queryKey: ['student', 'auth', 'my'],
    queryFn: getMy,
    ...options,
  });
};

const logout = (): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, null>('/student/auth/logout', {
    method: 'POST',
  });
};

export const useLogout = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, null>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, null>({
    mutationFn: logout,
    ...options, // 전달받은 옵션을 병합
  });
};

const forgotPassword = (
  data: ForgotPasswordRequestDto,
): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, ForgotPasswordRequestDto>(
    '/student/auth/forgot-password',
    {
      method: 'POST',
      data,
    },
  );
};

export const useForgotPassword = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, ForgotPasswordRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, ForgotPasswordRequestDto>({
    mutationFn: forgotPassword,
    ...options, // 전달받은 옵션을 병합
  });
};

const resetForgotPassword = (
  data: ResetForgotPasswordRequestDto,
): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, ResetForgotPasswordRequestDto>(
    '/student/auth/reset-password',
    {
      method: 'POST',
      data,
    },
  );
};

export const useResetForgotPassword = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, ResetForgotPasswordRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, ResetForgotPasswordRequestDto>({
    mutationFn: resetForgotPassword,
    ...options, // 전달받은 옵션을 병합
  });
};

const checkValidForgotPasswordToken = (
  token: string,
): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, Error>(`/student/auth/forgot-password/${token}`, {
    method: 'GET',
  });
};

export const useCheckValidForgotPasswordToken = (
  token: string,
  options?: Omit<UseQueryOptions<BaseResponseDto<EmptyResponseDto>, Error>, 'queryFn'>,
) => {
  return useQuery({
    queryKey: ['auth', 'forgot-password', token],
    queryFn: () => checkValidForgotPasswordToken(token),
    ...options,
  });
};

const sendVerifyEmail = (
  data: SendVerifyEmailRequestDto,
): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, SendVerifyEmailRequestDto>(
    '/student/auth/send-verify-email',
    {
      method: 'POST',
      data,
    },
  );
};

export const useSendVerifyEmail = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, SendVerifyEmailRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, SendVerifyEmailRequestDto>({
    mutationFn: sendVerifyEmail,
    ...options, // 전달받은 옵션을 병합
  });
};

const checkVerifyEmail = (
  data: CheckVerifyEmailRequestDto,
): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, CheckVerifyEmailRequestDto>(
    '/student/auth/check-verify-email',
    {
      method: 'POST',
      data,
    },
  );
};

export const useCheckVerifyEmail = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, CheckVerifyEmailRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, CheckVerifyEmailRequestDto>({
    mutationFn: checkVerifyEmail,
    ...options, // 전달받은 옵션을 병합
  });
};

const updateMy = (data: UpdateMyRequestDto): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, UpdateMyRequestDto>('/student/auth/my', {
    method: 'PATCH',
    data,
  });
};

export const useUpdateMy = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, UpdateMyRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, UpdateMyRequestDto>({
    mutationFn: updateMy,
    ...options, // 전달받은 옵션을 병합
  });
};

const withdraw = (): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, null>('/student/auth/withdraw', {
    method: 'DELETE',
  });
};

export const useWithdraw = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, void>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, void>({
    mutationFn: withdraw,
    ...options,
  });
};
